/* iter462 Phase 6 — Nutrition Lab "Препоръчани храни" tile.
 *
 * Lists foods the user pinned from /dashboard/nutrition-lab. Each row shows
 * FMS badge + Nutramilo indices + quick actions (open Lab, remove pin).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { FlaskConical, Star, ExternalLink, Loader2, Sparkles } from "lucide-react";

const fmsTone = (fms) => {
  if (fms >= 80) return "from-emerald-400 to-emerald-600";
  if (fms >= 65) return "from-sky-400 to-sky-600";
  if (fms >= 50) return "from-amber-400 to-amber-600";
  return "from-slate-400 to-slate-600";
};

export default function FoodLabRecommendedTile({ isDark, bg }) {
  const { API } = useAuth();
  const [items, setItems] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const fetchItems = () => {
    axios.get(`${API}/foodlab/recommended`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  };

  useEffect(() => { fetchItems(); }, [API]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeFood = async (foodId) => {
    setRemovingId(foodId);
    try {
      await axios.delete(`${API}/foodlab/recommended/${encodeURIComponent(foodId)}`);
      setItems((arr) => (arr || []).filter((f) => f.food_id !== foodId));
      toast.success(bg ? "Премахнато" : "Removed");
    } catch {
      toast.error(bg ? "Грешка" : "Error");
    } finally {
      setRemovingId(null);
    }
  };

  const card = isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-200";
  const txt = isDark ? "text-white" : "text-slate-900";
  const sub = isDark ? "text-slate-400" : "text-slate-500";

  return (
    <div className={`rounded-2xl border p-4 ${card}`} data-testid="foodlab-recommended-tile">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
            <FlaskConical className="w-4 h-4 text-white" />
          </div>
          <h3 className={`text-sm font-bold ${txt}`}>
            {bg ? "Препоръчани храни (Nutrition Lab)" : "Recommended foods (Nutrition Lab)"}
          </h3>
        </div>
        <Link
          to="/dashboard/nutrition-lab"
          className="text-[11px] flex items-center gap-1 text-violet-500 hover:underline"
          data-testid="foodlab-tile-open-lab"
        >
          {bg ? "Отвори Lab" : "Open Lab"}
          <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {/* Body */}
      {items === null && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className={`w-5 h-5 animate-spin ${sub}`} />
        </div>
      )}

      {items !== null && items.length === 0 && (
        <div className={`rounded-xl border border-dashed py-6 text-center ${isDark ? "border-slate-700" : "border-slate-200"}`}>
          <Sparkles className={`w-6 h-6 mx-auto mb-2 ${sub}`} />
          <p className={`text-xs ${sub}`}>
            {bg ? "Все още няма закачени храни." : "No pinned foods yet."}
          </p>
          <Link
            to="/dashboard/nutrition-lab"
            className="inline-block mt-2 text-[11px] font-semibold text-violet-500 hover:underline"
            data-testid="foodlab-tile-empty-cta"
          >
            {bg ? "Открий храни →" : "Discover foods →"}
          </Link>
        </div>
      )}

      {items !== null && items.length > 0 && (
        <div className="space-y-2">
          {items.slice(0, 6).map((f) => {
            const name = bg ? (f.name_bg || f.name_en) : (f.name_en || f.name_bg);
            return (
              <div
                key={f.food_id}
                data-testid={`foodlab-tile-item-${f.food_id}`}
                className={`flex items-center gap-2 rounded-lg p-2 ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`}
              >
                <div className={`shrink-0 px-2 py-1 rounded-md text-white text-xs font-bold bg-gradient-to-br ${fmsTone(f.fms)}`}>
                  {Math.round(f.fms || 0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-semibold truncate ${txt}`}>{name}</div>
                  <div className={`text-[10px] flex gap-2 ${sub}`}>
                    <span>DIL {f.indices?.dil ?? "—"}</span>
                    <span>NIS {f.indices?.nis ?? "—"}</span>
                    <span>UIS {f.indices?.uis ?? "—"}</span>
                  </div>
                </div>
                <button
                  onClick={() => removeFood(f.food_id)}
                  disabled={removingId === f.food_id}
                  data-testid={`foodlab-tile-remove-${f.food_id}`}
                  className="p-1 rounded-full text-amber-500 hover:bg-amber-500/10 disabled:opacity-50"
                  aria-label={bg ? "Премахни" : "Remove"}
                >
                  {removingId === f.food_id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Star className="w-4 h-4" fill="currentColor" />}
                </button>
              </div>
            );
          })}
          {items.length > 6 && (
            <div className={`text-[10px] text-center pt-1 ${sub}`}>
              +{items.length - 6} {bg ? "още" : "more"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
